import React, { useEffect, useRef, useState } from 'react';
import './FlikSelect.css';

/**
 * FLIK dropdown — one themed select used across the app so every dropdown looks
 * and behaves the same (a native <select>'s OS-drawn popup can't be themed).
 *
 * Two visual variants, both in the v3 language (snow-cut bevels, mono/amber):
 *  - "line": a line field for the cream form sheets (register / profile).
 *  - "box":  a bordered, beveled control for the dark app surfaces (admin).
 *
 * `options` is an array of `{ value, label }`. `onChange` receives the raw value
 * (not an event). When no option matches `value`, `placeholder` is shown.
 */
function ChevronIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="m6 9 6 6 6-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path d="m5 12 5 5 9-10" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function FlikSelect({
  value,
  onChange,
  options,
  placeholder = 'Selecciona',
  variant = 'line',
  invalid = false,
  ariaLabel,
  id,
  className = '',
}) {
  const [open, setOpen] = useState(false);
  const [activeIdx, setActiveIdx] = useState(-1);
  const ref = useRef(null);

  const selectedIdx = options.findIndex((o) => String(o.value) === String(value));
  const selected = selectedIdx >= 0 ? options[selectedIdx] : null;

  // Close on outside click / Escape while open.
  useEffect(() => {
    if (!open) return undefined;
    const onDocDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocDown);
    return () => document.removeEventListener('mousedown', onDocDown);
  }, [open]);

  // Highlight the current selection each time the panel opens.
  useEffect(() => {
    if (open) setActiveIdx(selectedIdx >= 0 ? selectedIdx : 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const choose = (v) => {
    onChange(v);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    switch (e.key) {
      case 'Escape':
        setOpen(false);
        break;
      case 'ArrowDown':
        e.preventDefault();
        if (!open) setOpen(true);
        else setActiveIdx((i) => Math.min(options.length - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        if (!open) setOpen(true);
        else setActiveIdx((i) => Math.max(0, i - 1));
        break;
      case 'Home':
        if (open) {
          e.preventDefault();
          setActiveIdx(0);
        }
        break;
      case 'End':
        if (open) {
          e.preventDefault();
          setActiveIdx(options.length - 1);
        }
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        if (!open) setOpen(true);
        else if (activeIdx >= 0) choose(options[activeIdx].value);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`FlikSelect FlikSelect--${variant}${open ? ' is-open' : ''}${
        invalid ? ' is-invalid' : ''
      }${className ? ` ${className}` : ''}`}
      ref={ref}
    >
      <button
        type="button"
        id={id}
        className="FlikSelect__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={onKeyDown}
      >
        <span className={`FlikSelect__value${selected ? '' : ' is-placeholder'}`}>
          {selected ? selected.label : placeholder}
        </span>
        <ChevronIcon />
      </button>

      {open && (
        <ul className="FlikSelect__panel" role="listbox" aria-label={ariaLabel}>
          {options.map((o, i) => {
            const active = i === selectedIdx;
            const highlighted = i === activeIdx;
            return (
              <li key={o.value === '' ? '__empty' : o.value} role="option" aria-selected={active}>
                <button
                  type="button"
                  className={`FlikSelect__option${active ? ' is-active' : ''}${
                    highlighted ? ' is-highlighted' : ''
                  }`}
                  onClick={() => choose(o.value)}
                  onMouseEnter={() => setActiveIdx(i)}
                >
                  <span>{o.label}</span>
                  {active && <CheckIcon />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

export default FlikSelect;
